import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";
import { 
  Mail, Phone, MapPin, Send, User, MessageSquare, 
  CheckCircle, AlertCircle, Linkedin, Github, Twitter,
  Link as LinkIcon
} from "lucide-react";

// Import UI components
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5 }
  }
};

const Contact = () => {
  const form = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState(null);
  const [category, setCategory] = useState("general");

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sendEmail = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    emailjs
      .sendForm(
        "service_r1qmz1g",
        "template_3nrdgfj",
        form.current,
        "BGkOpbq_V624LS8c9"
      )
      .then(
        (result) => {
          setFormStatus({ 
            type: "success", 
            message: "Your message has been sent successfully! I'll get back to you soon." 
          });
          form.current.reset();
          setCategory("general");
          setIsSubmitting(false);
        },
        (error) => {
          setFormStatus({ 
            type: "error", 
            message: "Something went wrong. Please try again or contact me directly via email." 
          });
          setIsSubmitting(false);
        }
      );
  };

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden bg-gradient-to-b from-blue-50/80 to-white dark:from-blue-900/20 dark:to-zinc-900">
        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-20 -left-10 w-72 h-72 bg-blue-200/10 dark:bg-blue-400/5 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 right-10 w-80 h-80 bg-indigo-200/10 dark:bg-indigo-400/5 rounded-full blur-3xl"></div>
          <svg className="absolute right-0 top-0 h-full text-zinc-200 dark:text-zinc-800/20 opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none" fill="currentColor">
            <polygon points="0,0 100,0 100,100" />
          </svg>
        </div>
        
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 mb-4 px-3 py-1">
              <span className="flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                <span>Let's Connect</span>
              </span>
            </Badge>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-3xl sm:text-4xl font-bold text-zinc-900 dark:text-white mb-6"
            >
              Get in <span className="text-blue-600 dark:text-blue-400">Touch</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-lg text-zinc-700 dark:text-zinc-300"
            >
              I'm always interested in new opportunities, collaborations, or just a friendly chat.
              Feel free to reach out using the form below or via direct contact methods.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Contact Information */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="lg:col-span-1 space-y-8"
            >
              {/* Contact Details */}
              <motion.div 
                variants={itemVariants}
                className="bg-white dark:bg-zinc-800/90 border border-zinc-200 dark:border-zinc-800 rounded-xl p-6 shadow-sm"
              >
                <h3 className="text-xl font-bold text-zinc-900 dark:text-white mb-5">
                  Contact Information
                </h3>
                
                <div className="space-y-4">
                  {/* Email */}
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-2 text-blue-600 dark:text-blue-400 mt-0.5">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Email</p>
                      <a 
                        href="mailto:casper.debock@gmail.com" 
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        casper.debock@gmail.com
                      </a>
                    </div>
                  </div>
                  
                  {/* Phone */}
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-2 text-blue-600 dark:text-blue-400 mt-0.5">
                      <Phone className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Phone</p>
                      <a 
                        href="tel:+32468171264" 
                        className="text-zinc-900 dark:text-zinc-100 hover:text-blue-600 dark:hover:text-blue-400"
                      >
                        +32 468 17 12 64
                      </a>
                    </div>
                  </div>
                  
                  {/* Location */}
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-2 text-blue-600 dark:text-blue-400 mt-0.5">
                      <MapPin className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Location</p>
                      <p className="text-zinc-900 dark:text-zinc-100">
                        Ghent, Belgium
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              {/* Social Links */}
              <motion.div 
                variants={itemVariants}
                className="bg-white dark:bg-zinc-800/90 border border-zinc-200 dark:border-zinc-800 rounded-xl p-6 shadow-sm"
              >
                <h3 className="text-xl font-bold text-zinc-900 dark:text-white mb-5">
                  Connect With Me
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                  <a 
                    href="https://linkedin.com/casperdebock" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-[#0077B5]/10 hover:bg-[#0077B5]/20 text-[#0077B5] dark:bg-[#0077B5]/20 dark:hover:bg-[#0077B5]/30 dark:text-[#0077B5] p-3 rounded-lg transition-colors"
                  >
                    <Linkedin className="h-5 w-5" />
                    <span className="font-medium">LinkedIn</span>
                  </a>
                  
                  <a 
                    href="https://github.com/CasperDeBock" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 dark:bg-zinc-700/50 dark:hover:bg-zinc-700 dark:text-zinc-300 p-3 rounded-lg transition-colors"
                  >
                    <Github className="h-5 w-5" />
                    <span className="font-medium">GitHub</span>
                  </a>
                  
                  <a 
                    href="https://twitter.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 text-[#1DA1F2] dark:bg-[#1DA1F2]/20 dark:hover:bg-[#1DA1F2]/30 dark:text-[#1DA1F2] p-3 rounded-lg transition-colors"
                  >
                    <Twitter className="h-5 w-5" />
                    <span className="font-medium">Twitter</span>
                  </a>
                  
                  <a 
                    href="https://casperdebock.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-blue-100 hover:bg-blue-200 text-blue-600 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-400 p-3 rounded-lg transition-colors"
                  >
                    <LinkIcon className="h-5 w-5" />
                    <span className="font-medium">Website</span>
                  </a>
                </div>
              </motion.div>
              
              {/* Availability */}
              <motion.div 
                variants={itemVariants}
                className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-xl p-6 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-white/20 rounded-full p-2">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold">Currently Available</h3>
                </div>
                <p className="text-white/90 mb-4">
                  I'm open to freelance opportunities, collaborations, and full-time positions.
                </p>
                <div className="bg-white/20 rounded-lg p-3 text-sm">
                  <p>Average response time: <span className="font-semibold">24 hours</span></p>
                </div>
              </motion.div>
            </motion.div>
            
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="bg-white dark:bg-zinc-800/90 border border-zinc-200 dark:border-zinc-800 rounded-xl p-6 md:p-8 shadow-sm"
              >
                <motion.h3 
                  variants={itemVariants}
                  className="text-xl font-bold text-zinc-900 dark:text-white mb-6 flex items-center gap-2"
                >
                  <MessageSquare className="h-5 w-5 text-blue-600" />
                  Send Me a Message
                </motion.h3>
                
                <AnimatePresence>
                  {formStatus && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className={`mb-6 p-4 rounded-lg ${
                        formStatus.type === "success" 
                          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" 
                          : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {formStatus.type === "success" ? (
                          <CheckCircle className="h-5 w-5 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="h-5 w-5 flex-shrink-0" />
                        )}
                        <p>{formStatus.message}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                <form ref={form} onSubmit={sendEmail} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Name Field */}
                    <motion.div variants={itemVariants}>
                      <Label htmlFor="name" className="text-zinc-700 dark:text-zinc-300 mb-1.5 block">
                        Name
                      </Label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User className="h-5 w-5 text-zinc-400" />
                        </div>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="John Doe"
                          required
                          className="pl-10 bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700"
                        />
                      </div>
                    </motion.div>
                    
                    {/* Email Field */}
                    <motion.div variants={itemVariants}>
                      <Label htmlFor="email" className="text-zinc-700 dark:text-zinc-300 mb-1.5 block">
                        Email
                      </Label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-zinc-400" />
                        </div>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="john@example.com"
                          required
                          className="pl-10 bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700"
                        />
                      </div>
                    </motion.div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Category Field */}
                    <motion.div variants={itemVariants}>
                      <Label htmlFor="category" className="text-zinc-700 dark:text-zinc-300 mb-1.5 block">
                        Subject Category
                      </Label>
                      <Select 
                        name="category" 
                        value={category} 
                        onValueChange={setCategory}
                      >
                        <SelectTrigger className="bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="question">Question</SelectItem>
                          <SelectItem value="proposal">Proposal</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </motion.div>
                    
                    {/* Subject Field */}
                    <motion.div variants={itemVariants}>
                      <Label htmlFor="subject" className="text-zinc-700 dark:text-zinc-300 mb-1.5 block">
                        Subject Title
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        placeholder="Brief overview of your message"
                        required
                        className="bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700"
                      />
                    </motion.div>
                  </div>
                  
                  {/* Message Field */}
                  <motion.div variants={itemVariants}>
                    <Label htmlFor="text" className="text-zinc-700 dark:text-zinc-300 mb-1.5 block">
                      Your Message
                    </Label>
                    <Textarea
                      id="text"
                      name="text"
                      placeholder="Write your message here..."
                      required
                      className="min-h-[150px] bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700"
                    />
                  </motion.div>
                  
                  {/* Submit Button */}
                  <motion.div variants={itemVariants} className="flex justify-end">
                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 rounded-lg transition-colors flex items-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </motion.div>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - Optional */}
      <section className="py-16 bg-zinc-50/80 dark:bg-zinc-800/30">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 mb-3 px-3 py-1">
              <span className="flex items-center gap-1.5">
                <MessageSquare className="h-3.5 w-3.5" />
                <span>Quick Questions</span>
              </span>
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white mb-4">
              Frequently Asked Questions
            </h3>
            <p className="text-zinc-600 dark:text-zinc-300">
              Here are answers to some common questions that might help you.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                q: "What services do you offer?",
                a: "I specialize in full-stack web development, UI/UX design, mobile app development, and technical consultancy. Each solution is tailored to meet your specific needs."
              },
              {
                q: "What is your typical response time?",
                a: "I typically respond to all inquiries within 24 hours during business days. For urgent matters, please mark your email as high priority."
              },
              {
                q: "Do you work with international clients?",
                a: "Absolutely! I work with clients worldwide and can accommodate different time zones for meetings and communication."
              },
              {
                q: "What is your preferred method of communication?",
                a: "Email is generally best for initial contact and detailed discussions, but I'm also available for video calls once we establish communication."
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white dark:bg-zinc-800 rounded-xl shadow-sm p-6 border border-zinc-200 dark:border-zinc-700"
              >
                <h4 className="font-semibold text-lg text-zinc-900 dark:text-white mb-2">
                  {item.q}
                </h4>
                <p className="text-zinc-600 dark:text-zinc-400">
                  {item.a}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;